// Quiet Hours (Settings > Notifications) — module-level singleton so any sound
// player can check it without context plumbing. Fail-open: not quiet until prefs load.
let prefs = {
  quiet_hours_enabled: false,
  quiet_hours_start: '22:00',
  quiet_hours_end: '07:00',
  quiet_hours_weekend_enabled: false,
  quiet_hours_weekend_start: '23:00',
  quiet_hours_weekend_end: '09:00',
  critical_override: true,
};

const listeners = new Set();

export const setQuietHoursPrefs = (p) => {
  if (!p) return;
  prefs = {
    quiet_hours_enabled: p.quiet_hours_enabled ?? prefs.quiet_hours_enabled,
    quiet_hours_start: p.quiet_hours_start || prefs.quiet_hours_start,
    quiet_hours_end: p.quiet_hours_end || prefs.quiet_hours_end,
    quiet_hours_weekend_enabled: p.quiet_hours_weekend_enabled ?? prefs.quiet_hours_weekend_enabled,
    quiet_hours_weekend_start: p.quiet_hours_weekend_start || prefs.quiet_hours_weekend_start,
    quiet_hours_weekend_end: p.quiet_hours_weekend_end || prefs.quiet_hours_weekend_end,
    critical_override: p.critical_override ?? prefs.critical_override,
  };
  listeners.forEach((fn) => fn());
};

// Subscribe to pref changes (returns unsubscribe). Used by the header badge.
export const onQuietHoursPrefsChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getQuietHoursPrefs = () => ({ ...prefs });

const toMins = (hhmm, fallback) => {
  const [h, m] = String(hhmm || fallback).split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

// The window that applies right now: weekend (Sat/Sun) override when enabled.
export const getActiveQuietWindow = () => {
  const day = new Date().getDay(); // 0 Sun, 6 Sat
  const weekend = prefs.quiet_hours_weekend_enabled && (day === 0 || day === 6);
  return weekend
    ? { start: prefs.quiet_hours_weekend_start, end: prefs.quiet_hours_weekend_end, weekend: true }
    : { start: prefs.quiet_hours_start, end: prefs.quiet_hours_end, weekend: false };
};

export const isQuietNow = () => {
  if (!prefs.quiet_hours_enabled) return false;
  const win = getActiveQuietWindow();
  const s = toMins(win.start, '22:00');
  const e = toMins(win.end, '07:00');
  if (s === e) return false;
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  return s < e ? mins >= s && mins < e : mins >= s || mins < e;
};

// Suppress a sound? Critical-severity alerts still play when "Allow critical alerts" is on.
export const shouldSuppressSound = (severity = null) =>
  isQuietNow() && !(severity === 'critical' && prefs.critical_override !== false);
